import { inject, Injectable } from '@angular/core';
import { concat, defer, last, Observable, of, switchMap } from 'rxjs';

import { CompanyInfo } from '@core/models';

import { CompanyApiService } from './company-api.service';
import { CompanyDto, SaveCompanyProfileDto } from './company.dto';
import { iconToDto, phaseStatusToDto } from './company.mapper';

/**
 * Persists a whole Company-overview editing session in one go, when the user
 * clicks Done.
 *
 * Why batch rather than save as you type: the overview's inputs fire on every
 * keystroke, so a per-change save turns "Denver" into six requests. Editing stays
 * local — exactly as it behaves today — and this replays the difference against
 * the API at the end.
 *
 * The order of operations is not arbitrary:
 *
 *   profile → per list: deletes, then updates, then creates → then reorder
 *
 * Deletes go first so a removed row can't collide with a rename. Creates go last
 * before the reorder because a new row lands at the END, so its guid only exists
 * once it has been made — and the reorder has to name EVERY row, including the
 * new ones, or the server rejects the set as not matching its records.
 *
 * Every call returns the whole page, so the LAST response is the new truth and
 * the caller replaces its state with it. Anything that fails aborts the rest:
 * a half-applied session the user can still see and correct beats one silently
 * half-written.
 */
@Injectable({ providedIn: 'root' })
export class CompanySyncService {
  private readonly api = inject(CompanyApiService);

  /**
   * Replay `working` onto the server, given the `baseline` last loaded from it.
   * Emits the final `CompanyDto`, or null when nothing needed saying.
   */
  save(baseline: CompanyInfo, working: CompanyInfo): Observable<CompanyDto | null> {
    const steps: Array<() => Observable<CompanyDto>> = [];

    const profile = profileDiff(baseline, working);
    if (profile) steps.push(() => this.api.patchProfile(profile));

    // Pillars ────────────────────────────────────────────────────────────────
    for (const guid of removedGuids(baseline.diamondStandard, working.diamondStandard, (p) => p.pillarGuid)) {
      steps.push(() => this.api.deletePillar(guid));
    }
    for (const p of working.diamondStandard) {
      const was = findByGuid(baseline.diamondStandard, p.pillarGuid, (x) => x.pillarGuid);
      if (!p.pillarGuid) {
        steps.push(() => this.api.createPillar({ pillarName: p.name, description: p.desc }));
      } else if (was && (was.name !== p.name || was.desc !== p.desc)) {
        steps.push(() => this.api.updatePillar(p.pillarGuid!, { pillarName: p.name, description: p.desc }));
      }
    }

    // Core values ────────────────────────────────────────────────────────────
    for (const guid of removedGuids(baseline.coreValues, working.coreValues, (v) => v.coreValueGuid)) {
      steps.push(() => this.api.deleteCoreValue(guid));
    }
    for (const v of working.coreValues) {
      const was = findByGuid(baseline.coreValues, v.coreValueGuid, (x) => x.coreValueGuid);
      const body = { title: v.title, icon: iconToDto(v.icon), body: v.body };
      if (!v.coreValueGuid) {
        steps.push(() => this.api.createCoreValue(body));
      } else if (was && (was.title !== v.title || was.icon !== v.icon || was.body !== v.body)) {
        steps.push(() => this.api.updateCoreValue(v.coreValueGuid!, body));
      }
    }

    // Growth phases ──────────────────────────────────────────────────────────
    for (const guid of removedGuids(baseline.phases, working.phases, (p) => p.phaseGuid)) {
      steps.push(() => this.api.deletePhase(guid));
    }
    for (const ph of working.phases) {
      const was = findByGuid(baseline.phases, ph.phaseGuid, (x) => x.phaseGuid);
      // `growthPhaseStatus` is deliberately NOT sent on an update: omitting it
      // leaves the phase's status alone, so fixing a typo can't move the company
      // out of the phase it is in. Entering a phase is its own call.
      const body = {
        label: ph.label,
        targetRevenue: ph.targetRevenue,
        yearRange: ph.years,
        description: ph.description,
      };
      if (!ph.phaseGuid) {
        steps.push(() => this.api.createPhase({ ...body, growthPhaseStatus: phaseStatusToDto(ph.status) }));
      } else if (
        was &&
        (was.label !== ph.label ||
          was.targetRevenue !== ph.targetRevenue ||
          was.years !== ph.years ||
          was.description !== ph.description)
      ) {
        steps.push(() => this.api.updatePhase(ph.phaseGuid!, body));
      }
    }

    // Entering a phase is not a "step" — it is resolved at the end against the
    // server's own page — so it has to be counted here, or a session whose only
    // change was the current phase would save nothing at all.
    const phaseChanged = working.currentPhase !== baseline.currentPhase;
    if (!steps.length && !phaseChanged) return of(null);

    // Run strictly in sequence — each response supersedes the last — then reorder
    // against the page as it finally stands.
    const writes = concat(...steps.map((run) => defer(run)));
    return concat(
      writes,
      defer(() => this.reorderAll(working)),
      defer(() => this.enterChosenPhase(baseline, working)),
    ).pipe(last());
  }

  /**
   * Move the company into the phase chosen during the session, if it changed.
   *
   * LAST, and addressed by resolving the phase rather than by the number the
   * client was holding: `/enter` takes a sortIndex, and a reorder or a deletion
   * earlier in this same save may have changed which phase that number refers
   * to. The phase is matched by guid — or by label for one created in this
   * session — against the page as the server now has it, and its CURRENT
   * sortIndex is what gets sent.
   */
  private enterChosenPhase(baseline: CompanyInfo, working: CompanyInfo): Observable<CompanyDto> {
    return this.api.get().pipe(
      switchMap((page) => {
        if (working.currentPhase === baseline.currentPhase) return of(page);
        const chosen = working.phases.find((p) => p.num === working.currentPhase);
        if (!chosen) return of(page);
        const onRecord =
          page.companyGrowthPhases.find((p) => chosen.phaseGuid && p.phaseGuid === chosen.phaseGuid) ??
          page.companyGrowthPhases.find((p) => p.label === chosen.label);
        // Already current, or the phase can't be found: nothing to say.
        if (!onRecord || onRecord.growthPhaseStatus === 'CURRENT') return of(page);
        return this.api.enterPhase(onRecord.sortIndex);
      }),
    );
  }

  /**
   * Put each list into the order the user left it in, once every row exists.
   *
   * Newly created rows have no guid on the client, so the desired order is
   * resolved against the page the server just returned: existing rows match by
   * guid, and created ones match by name among the rows not already claimed.
   * Nothing is sent for a list whose order already agrees.
   */
  private reorderAll(working: CompanyInfo): Observable<CompanyDto> {
    return this.api.get().pipe(
      switchMap((page) => {
        const steps: Array<Observable<CompanyDto>> = [];

        const pillars = resolveOrder(
          working.diamondStandard.map((p) => ({ guid: p.pillarGuid, name: p.name })),
          page.diamondStandardPillars.map((p) => ({ guid: p.pillarGuid, name: p.pillarName })),
        );
        if (pillars) steps.push(this.api.reorderPillars(pillars));

        const values = resolveOrder(
          working.coreValues.map((v) => ({ guid: v.coreValueGuid, name: v.title })),
          page.coreValues.map((v) => ({ guid: v.coreValueGuid, name: v.title })),
        );
        if (values) steps.push(this.api.reorderCoreValues(values));

        const phases = resolveOrder(
          working.phases.map((p) => ({ guid: p.phaseGuid, name: p.label })),
          page.companyGrowthPhases.map((p) => ({ guid: p.phaseGuid, name: p.label })),
        );
        if (phases) steps.push(this.api.reorderPhases(phases));

        // Every list already agrees — the page we just read IS the answer.
        return steps.length ? concat(...steps).pipe(last()) : of(page);
      }),
    );
  }
}

/** The profile keys that changed, or null when none did. */
function profileDiff(was: CompanyInfo, now: CompanyInfo): SaveCompanyProfileDto | null {
  const body: SaveCompanyProfileDto = {};
  if (was.legalName !== now.legalName) body.legalName = now.legalName;
  if (was.shortName !== now.shortName) body.shortName = now.shortName;
  if (was.tagline !== now.tagline) body.tagline = now.tagline;
  if (was.mission !== now.mission) body.mission = now.mission;
  if (was.vision !== now.vision) body.vision = now.vision;
  if (was.videoUrl !== now.videoUrl) body.welcomeVideoUrl = now.videoUrl;
  if (was.hq !== now.hq) body.headquarters = now.hq;
  // Sent even though the contract calls it read-only — the UI offers it, so the
  // request has to carry it rather than the edit vanishing. See SaveCompanyProfileDto.
  if (was.founded !== now.founded) body.foundedDt = now.founded;
  return Object.keys(body).length ? body : null;
}

/** Guids present in the baseline but gone from the working copy. */
function removedGuids<T>(was: T[], now: T[], guidOf: (row: T) => string | undefined): string[] {
  const kept = new Set(now.map(guidOf).filter(Boolean) as string[]);
  return (was.map(guidOf).filter(Boolean) as string[]).filter((g) => !kept.has(g));
}

function findByGuid<T>(rows: T[], guid: string | undefined, guidOf: (row: T) => string | undefined): T | undefined {
  return guid ? rows.find((r) => guidOf(r) === guid) : undefined;
}

/**
 * The guid order a list should end up in, or null when it already agrees.
 *
 * `desired` is what the user sees; `onRecord` is what the server just returned.
 * A row the client created has no guid, so it is matched by name among the rows
 * the server has that nothing else claimed. If the two sets can't be reconciled
 * — a row appeared or vanished underneath us — this returns null and leaves the
 * order alone rather than sending a set the server would reject.
 */
function resolveOrder(
  desired: Array<{ guid?: string; name: string }>,
  onRecord: Array<{ guid: string; name: string }>,
): string[] | null {
  if (desired.length !== onRecord.length) return null;
  const unclaimed = new Map<string, string[]>();
  for (const r of onRecord) {
    if (!unclaimed.has(r.name)) unclaimed.set(r.name, []);
    unclaimed.get(r.name)!.push(r.guid);
  }
  const known = new Set(onRecord.map((r) => r.guid));
  const out: string[] = [];
  for (const row of desired) {
    if (row.guid && known.has(row.guid)) {
      out.push(row.guid);
      const bucket = unclaimed.get(row.name);
      if (bucket) {
        const at = bucket.indexOf(row.guid);
        if (at >= 0) bucket.splice(at, 1);
      }
      continue;
    }
    const bucket = unclaimed.get(row.name);
    const guid = bucket?.shift();
    if (!guid) return null;
    out.push(guid);
  }
  const same = out.length === onRecord.length && out.every((g, i) => g === onRecord[i].guid);
  return same ? null : out;
}
